/**
 * SCR-016 — Danh sách báo cáo tiến độ (admin + manager).
 * Mobile-first: card list <md, table ≥md.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MoreHorizontal, Plus, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@client/components/data-table';
import { ReportForm, type ReportFormValues } from '@client/components/forms/report-form';
import { isApiError, useBranches } from '@client/lib/queries/use-branches';
import {
  type ProgressReport,
  type ReportStatus,
  type ReportPeriod,
  progressReportKeys,
  useProgressReports,
  useCreateProgressReport,
  useDeleteProgressReport,
} from '@client/lib/queries/use-progress-reports';
import { useBranchFilter } from '@client/store/branch-filter';
import { api } from '@client/lib/api-client';
import { useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/admin/reports/')({
  component: ReportsListPage,
});

const PERIOD_LABELS: Record<ReportPeriod, string> = {
  monthly: 'Hàng tháng',
  midterm: 'Giữa kỳ',
  final: 'Cuối kỳ',
  custom: 'Tùy chỉnh',
};

const STATUS_META: Record<ReportStatus, { label: string; variant: 'secondary' | 'success' }> = {
  draft: { label: 'Nháp', variant: 'secondary' },
  published: { label: 'Đã công bố', variant: 'success' },
};

const RATING_META: Record<string, { label: string; variant: 'success' | 'info' | 'secondary' | 'warning' }> = {
  excellent: { label: 'Xuất sắc', variant: 'success' },
  good: { label: 'Tốt', variant: 'info' },
  average: { label: 'Trung bình', variant: 'secondary' },
  needs_improvement: { label: 'Cần cải thiện', variant: 'warning' },
};

function ReportsListPage() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { role?: string } };
  };
  const role = session?.user?.role ?? '';
  const qc = useQueryClient();
  const { selectedBranchId } = useBranchFilter();

  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [periodFilter, setPeriodFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editReport, setEditReport] = useState<ProgressReport | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const effectiveBranch =
    role === 'manager'
      ? undefined
      : branchFilter !== 'all'
        ? branchFilter
        : selectedBranchId ?? undefined;

  const params = {
    page,
    pageSize: PAGE_SIZE,
    branchId: effectiveBranch,
    period: periodFilter !== 'all' ? (periodFilter as ReportPeriod) : undefined,
    status: statusFilter !== 'all' ? (statusFilter as ReportStatus) : undefined,
    // Note: server listProgressReportsSchema does not support a free-text search param.
    // Search is currently client-side filtering only via DataTable searchValue.
  };

  const { data, isLoading, isError, refetch } = useProgressReports(params);
  const { data: branchesData } = useBranches({ status: 'active', pageSize: 100 });
  const branches = branchesData?.items ?? [];
  const createReport = useCreateProgressReport();
  const deleteReport = useDeleteProgressReport();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  async function handleSubmit(values: ReportFormValues) {
    setFormError(null);
    setIsSubmitting(true);
    try {
      if (editReport) {
        await api.patch(`/api/progress-reports/${editReport.id}`, values);
        await qc.invalidateQueries({ queryKey: progressReportKeys.all });
        toast.success('Đã cập nhật báo cáo.');
      } else {
        await createReport.mutateAsync(values);
        toast.success('Đã tạo báo cáo tiến độ.');
      }
      setDialogOpen(false);
    } catch (err) {
      setFormError(isApiError(err) ? err.message : 'Lỗi máy chủ, thử lại sau.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Xóa báo cáo nháp này?')) return;
    try {
      await deleteReport.mutateAsync(id);
      toast.success('Đã xóa báo cáo.');
    } catch (err) {
      const msg = isApiError(err) && err.status === 422
        ? 'Không thể xóa báo cáo đã công bố.'
        : 'Lỗi khi xóa báo cáo.';
      toast.error(msg);
    }
  }

  const columns: ColumnDef<ProgressReport>[] = [
    {
      accessorKey: 'studentName',
      header: 'Học viên',
      cell: ({ row }) => (
        <Link
          to="/admin/reports/$reportId"
          params={{ reportId: row.original.id }}
          className="hover:underline font-medium"
        >
          {row.original.studentName ?? row.original.enrollmentId.slice(0, 8)}
        </Link>
      ),
    },
    { accessorKey: 'className', header: 'Lớp', cell: ({ row }) => row.original.className ?? '—' },
    {
      accessorKey: 'period',
      header: 'Kỳ',
      cell: ({ row }) =>
        `${PERIOD_LABELS[row.original.period] ?? row.original.period}${row.original.periodLabel ? ` — ${row.original.periodLabel}` : ''}`,
    },
    {
      accessorKey: 'overallRating',
      header: 'Đánh giá',
      cell: ({ row }) => {
        const r = row.original.overallRating;
        if (!r) return '—';
        const m = RATING_META[r];
        return <Badge variant={m?.variant} className="text-xs">{m?.label ?? r}</Badge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Trạng thái',
      cell: ({ row }) => {
        const m = STATUS_META[row.original.status];
        return <Badge variant={m.variant} className="text-xs">{m.label}</Badge>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Ngày tạo',
      cell: ({ row }) =>
        row.original.createdAt ? format(new Date(row.original.createdAt), 'dd/MM/yyyy') : '—',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to="/admin/reports/$reportId" params={{ reportId: row.original.id }}>
                Xem chi tiết
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => { setEditReport(row.original); setFormError(null); setDialogOpen(true); }}>
              Sửa
            </DropdownMenuItem>
            {row.original.status === 'draft' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => void handleDelete(row.original.id)}
                >
                  Xóa
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-foreground">Báo cáo tiến độ</h1>
        <Button
          className="bg-accent text-accent-foreground hover:bg-accent/90 h-10 self-start sm:self-auto"
          onClick={() => { setEditReport(null); setFormError(null); setDialogOpen(true); }}
        >
          <Plus className="h-4 w-4 mr-1.5" /> Tạo báo cáo
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => void refetch()}
        emptyMessage={search || periodFilter !== 'all' || statusFilter !== 'all' ? 'Không tìm thấy báo cáo nào.' : 'Chưa có báo cáo nào.'}
        searchPlaceholder="Tìm tên học viên..."
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        pagination={total > 0 ? { page, pageSize: PAGE_SIZE, total } : undefined}
        onPageChange={setPage}
        filterSlot={
          <div className="flex flex-wrap gap-2">
            {role === 'admin' && (
              <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setPage(1); }}>
                <SelectTrigger className="h-10 w-[150px]">
                  <SelectValue placeholder="Cơ sở" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả cơ sở</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={periodFilter} onValueChange={(v) => { setPeriodFilter(v); setPage(1); }}>
              <SelectTrigger className="h-10 w-[140px]">
                <SelectValue placeholder="Kỳ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả kỳ</SelectItem>
                {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="h-10 w-[140px]">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => void refetch()} title="Làm mới">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editReport ? 'Sửa báo cáo' : 'Tạo báo cáo tiến độ'}</DialogTitle>
          </DialogHeader>
          {formError && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}
          <ReportForm
            isEdit={!!editReport}
            isSubmitting={isSubmitting}
            defaultValues={editReport ? {
              period: editReport.period,
              periodLabel: editReport.periodLabel ?? '',
              periodStart: editReport.periodStart ?? '',
              periodEnd: editReport.periodEnd ?? '',
              attendanceSummary: editReport.attendanceSummary ?? '',
              academicComment: editReport.academicComment ?? '',
              behaviorComment: editReport.behaviorComment ?? '',
              recommendation: editReport.recommendation ?? '',
              overallRating: editReport.overallRating ?? '',
              status: editReport.status,
            } : undefined}
            onSubmit={handleSubmit}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
